/**
 * DOM指标空状态监控系统 - 简化DOM扫描器
 */

import type { 
  ISimpleDOMScannerOptions, 
  IElementConfig, 
  IScanResult,
  IPresetSelector
} from '../types/simple';
import { SIMPLE_BUSINESS_DATA_PREFIXES } from '../types/simple';
import { PathMatcher, PathMatchCache } from './utils/pathMatcher';

/**
 * 简化的DOM扫描器
 * 负责扫描通过data属性标记的元素和预设CSS选择器的元素
 */
export class SimpleDOMScanner {
  private options: ISimpleDOMScannerOptions;
  private scanCount = 0;

  constructor(options: ISimpleDOMScannerOptions) {
    this.options = options;
  }

  /**
   * 扫描监控元素（包括标记元素和预设选择器元素）
   */
  scanMetricsElements(): IScanResult {
    const startTime = Date.now();
    
    try {
      const allElements: IElementConfig[] = [];
      const processedElements = new Set<Element>(); // 避免重复处理同一个元素
      
      // 1. 扫描标记的元素
      const markedElements = this.scanMarkedElements();
      markedElements.forEach(config => {
        allElements.push(config);
        processedElements.add(config.element);
      });
      
      // 2. 扫描预设选择器元素
      const presetElements = this.scanPresetElements();
      presetElements.forEach(config => {
        // 只添加未被处理过的元素（避免重复）
        if (!processedElements.has(config.element)) {
          allElements.push(config);
          processedElements.add(config.element);
        }
      });
      
      this.scanCount++;
      
      console.log(`[SimpleDOMScanner] 扫描完成: 标记元素${markedElements.length}个, 预设元素${presetElements.length}个, 总计${allElements.length}个`);
      
      return {
        elements: allElements,
        scanTime: startTime,
        totalCount: allElements.length
      };
      
    } catch (error) {
      console.warn('[SimpleDOMScanner] 扫描失败:', error);
      return {
        elements: [],
        scanTime: startTime,
        totalCount: 0
      };
    }
  }

  /**
   * 扫描标记的元素
   */
  private scanMarkedElements(): IElementConfig[] {
    const markedElements = document.querySelectorAll(this.options.markedSelector);
    const ignoredElements = document.querySelectorAll(this.options.ignoreSelector);
    
    // 过滤掉被忽略的元素
    const validElements = Array.from(markedElements).filter(element => 
      !this.isIgnored(element, ignoredElements)
    );
    
    // 提取元素配置
    return validElements.map(element => this.extractElementConfig(element));
  }

  /**
   * 检查预设选择器是否应该在当前路径生效
   * @param preset 预设选择器配置
   * @param currentPath 当前页面路径
   * @returns 是否应该应用此预设选择器
   */
  private shouldApplyPresetSelector(preset: IPresetSelector, currentPath: string): boolean {
    // 检查缓存
    const cached = PathMatchCache.getCached(currentPath, preset.pathPattern, preset.excludePaths);
    if (cached !== null) {
      return cached;
    }

    // 执行路径匹配
    const shouldApply = PathMatcher.shouldApply(currentPath, preset.pathPattern, preset.excludePaths);
    
    // 缓存结果
    PathMatchCache.setCached(currentPath, preset.pathPattern, preset.excludePaths, shouldApply);
    
    return shouldApply;
  }

  /**
   * 扫描预设选择器元素 - 父级容器 + 双选择器模式
   */
  private scanPresetElements(): IElementConfig[] {
    const presetElements: IElementConfig[] = [];
    
    if (!this.options.presetSelectors || this.options.presetSelectors.length === 0) {
      return presetElements;
    }
    
    // 获取当前页面路径
    const currentPath = PathMatcher.getCurrentPath();
    const ignoredElements = document.querySelectorAll(this.options.ignoreSelector);
    
    this.options.presetSelectors.forEach((preset, presetIndex) => {
      try {
        // 0. 检查路径匹配规则
        if (!this.shouldApplyPresetSelector(preset, currentPath)) {
          return; // 路径不匹配，跳过此预设选择器
        }
        
        // 1. 先找到所有父级容器
        const containers = document.querySelectorAll(preset.containerSelector);
        
        Array.from(containers).forEach((container, containerIndex) => {
          // 2. 在每个容器内查找指标元素
          const metricElement = container.querySelector(preset.metricSelector);
          if (!metricElement) {
            return; // 容器内没有指标元素，跳过
          }
          
          // 跳过被忽略的元素
          if (this.isIgnored(metricElement, ignoredElements)) {
            return;
          }
          
          // 跳过已经有data-metrics-monitor标记的元素（避免重复）
          if (metricElement.hasAttribute('data-metrics-monitor')) {
            return;
          }
          
          // 3. 在容器内查找名称元素并提取名称
          const nameElement = container.querySelector(preset.nameSelector);
          const extractedName = nameElement?.textContent?.trim();
          const finalName = extractedName || 
                           `${preset.defaultName || '预设指标'}_${containerIndex + 1}`;
          
          // 4. 生成元素配置
          const config = this.extractPresetElementConfig(
            metricElement, 
            preset, 
            finalName, 
            containerIndex
          );
          presetElements.push(config);
        });
        
      } catch (error) {
        console.warn(`[SimpleDOMScanner] 预设选择器容器 "${preset.containerSelector}" 扫描失败:`, error);
      }
    });
    
    return presetElements;
  }

  /**
   * 检查元素是否被忽略
   */
  private isIgnored(element: Element, ignoredElements: NodeListOf<Element>): boolean {
    return Array.from(ignoredElements).includes(element);
  }

  /**
   * 提取元素配置信息
   */
  private extractElementConfig(element: Element): IElementConfig {
    const name = element.getAttribute('data-metrics-name') || '未命名指标';
    
    return {
      elementId: this.generateElementId(element),
      name,
      selector: this.generateSelector(element),
      businessData: this.extractBusinessData(element),
      element
    };
  }

  /**
   * 提取预设选择器元素配置信息
   */
  private extractPresetElementConfig(
    element: Element, 
    preset: IPresetSelector, 
    finalName: string, 
    index: number
  ): IElementConfig {
    return {
      elementId: this.generatePresetElementId(element, preset, finalName, index),
      name: finalName,
      selector: this.generateSelector(element),
      businessData: {
        ...preset.businessData,
        presetContainer: preset.containerSelector,
        presetMetric: preset.metricSelector,
        presetName: preset.nameSelector,
        ...this.extractBusinessData(element) // 元素自身的业务数据优先级更高
      },
      element
    };
  }

  /**
   * 生成预设选择器元素的唯一标识
   */
  private generatePresetElementId(
    element: Element, 
    preset: IPresetSelector, 
    finalName: string, 
    index: number
  ): string {
    const rect = element.getBoundingClientRect();
    const timestamp = Date.now();
    const containerHash = preset.containerSelector.replace(/[^a-zA-Z0-9]/g, '_');
    const nameHash = finalName.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_'); // 支持中文
    
    return `preset_${containerHash}_${nameHash}_${index}_${Math.round(rect.top)}_${Math.round(rect.left)}_${timestamp}`;
  }

  /**
   * 生成元素唯一标识
   */
  private generateElementId(element: Element): string {
    // 使用元素的位置信息生成唯一标识
    const rect = element.getBoundingClientRect();
    const name = element.getAttribute('data-metrics-name') || 'unnamed';
    const timestamp = Date.now();
    
    return `${name}_${Math.round(rect.top)}_${Math.round(rect.left)}_${timestamp}`;
  }

  /**
   * 生成CSS选择器路径（最多向上取三级）
   */
  private generateSelector(element: Element): string {
    const path: string[] = [];
    let current: Element | null = element;
    let level = 0;
    const maxLevels = 3;
    
    while (current && current !== document.body && level < maxLevels) {
      let selector = current.tagName.toLowerCase();
      
      // 添加ID（如果有ID则可以提前结束）
      if (current.id) {
        selector += `#${current.id}`;
        path.unshift(selector);
        break;
      }
      
      // 添加类名
      if (current.className) {
        const classes = current.className.split(' ').filter(cls => cls);
        if (classes.length > 0) {
          // 最多取前2个类名，避免选择器过长
          selector += `.${classes.slice(0, 2).join('.')}`;
        }
      }
      
      path.unshift(selector);
      current = current.parentElement;
      level++;
    }
    
    return path.join(' > ');
  }

  /**
   * 提取业务数据
   */
  private extractBusinessData(element: Element): Record<string, any> | undefined {
    const businessData: Record<string, any> = {};
    
    for (const attr of Array.from(element.attributes)) {
      if (SIMPLE_BUSINESS_DATA_PREFIXES.some(prefix => prefix === attr.name)) {
        const key = attr.name.replace('data-', '');
        businessData[key] = attr.value;
      }
    }
    
    return Object.keys(businessData).length > 0 ? businessData : undefined;
  }

  /**
   * 获取扫描统计
   */
  getStats(): { scanCount: number } {
    return { scanCount: this.scanCount };
  }

  /**
   * 重置统计
   */
  resetStats(): void {
    this.scanCount = 0;
  }
} 